import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../components/layout/MainLayout';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import ProfileHeader from '../../candidate-profile/components/ProfileHeader';
import CandidateCallsTable from '../components/CandidateCallsTable';
import { useCandidate } from '../../../hooks';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getCandidateAlerts,
  getCandidateCalls,
  deleteCandidate,
  getPrioritizedCandidatesCount,
  uploadCandidatesToZohoList,
} from '../../../lib/api';
import { buildCandidateProfileView } from '../../../lib/utils/candidateProfileView';
import { getCandidateProfilePath } from '../../../lib/utils/candidateRoutes';
import { AI_CALLS_PATH } from '../../../lib/utils/aiCallsRoutes';
import ZohoUploadResult from '../../../components/ZohoUploadResult';

const MAX_PRIORITIZED = 5;

const CandidateAICallsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateId } = useParams();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadNotice, setUploadNotice] = useState(null);

  const returnPath = location.state?.returnTo || AI_CALLS_PATH;

  const { candidate: dbCandidate, loading, error, updateCandidate } = useCandidate(candidateId);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!candidateId) return;

      try {
        setLoadingDetails(true);
        const [alertsData, callsData] = await Promise.all([
          getCandidateAlerts(candidateId),
          getCandidateCalls(candidateId),
        ]);
        setAlerts(alertsData || []);
        setCalls(callsData || []);
      } catch (err) {
        console.error('Error fetching candidate call details:', err);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [candidateId]);

  const candidate = useMemo(
    () => buildCandidateProfileView(dbCandidate, alerts, calls),
    [dbCandidate, alerts, calls]
  );

  const handleBack = () => {
    navigate(returnPath);
  };

  const handleViewProfile = () => {
    navigate(getCandidateProfilePath(candidateId));
  };

  const handleSyncToATS = async () => {
    if (!user?.id || syncing) return;

    const zohoId = String(dbCandidate?.zoho_id ?? '').trim();
    if (!zohoId) return;

    setUploadNotice(null);
    setUploadResult(null);
    setSyncing(true);
    try {
      const result = await uploadCandidatesToZohoList(user.id, [zohoId]);
      setUploadResult(result);
    } catch (err) {
      console.error('Error syncing to ATS:', err);
      setUploadNotice({
        type: 'error',
        message: err?.message || t('candidateProfile.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateContact = async ({ email, phone }) => {
    if (!candidateId) return;

    await updateCandidate({
      email: email || null,
      phone: phone || null,
    });
  };

  const handleTogglePriority = async () => {
    if (!candidateId || !candidate) return;

    const newPriorityValue = !candidate.isPrioritized;

    if (newPriorityValue && user?.id) {
      try {
        const prioritizedCount = await getPrioritizedCandidatesCount(user.id);
        if (prioritizedCount >= MAX_PRIORITIZED) {
          setShowLimitDialog(true);
          return;
        }
      } catch (err) {
        console.warn('getPrioritizedCandidatesCount failed:', err);
      }
    }

    try {
      await updateCandidate({ prioritize: newPriorityValue }, { updateTimestamp: false });
    } catch (err) {
      console.error('Failed to update priority:', err);
    }
  };

  const handleDeleteCandidate = async () => {
    if (!candidateId) return;

    try {
      await deleteCandidate(candidateId);
      navigate(returnPath);
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert(t('ai.candidate.deleteFailed'));
    }
  };

  if (!candidateId) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('ai.candidate.noSelected')}</h2>
            <button
              type="button"
              onClick={handleBack}
              className="text-primary hover:text-accent underline"
            >
              {t('ai.candidate.backToCalls')}
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading || loadingDetails) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('ai.candidate.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !candidate) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-foreground mb-2">{t('ai.candidate.loadFailed')}</h2>
            <p className="text-muted-foreground mb-4">{error || t('ai.candidate.notFound')}</p>
            <button
              type="button"
              onClick={handleBack}
              className="text-primary hover:text-accent underline"
            >
              {t('ai.candidate.backToCalls')}
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('ai.candidate.helmetTitle', { name: candidate.name })}</title>
        <meta
          name="description"
          content={t('ai.candidate.helmetDescription', { name: candidate.name })}
        />
      </Helmet>
      <MainLayout>
        <div className="min-h-screen bg-background">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBack}
              className="mb-4 bg-white hover:bg-accent hover:text-accent-foreground"
            >
              {t('common.back')}
            </Button>

            <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  {t('ai.candidate.title')}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t('ai.candidate.subtitle')}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleViewProfile}
                iconName="ArrowRight"
                iconPosition="right"
                className="shrink-0"
              >
                {t('ai.candidate.profile')}
              </Button>
            </div>

            <div className="space-y-4 md:space-y-6 lg:space-y-8">
              <ProfileHeader
                candidate={candidate}
                onDeleteCandidate={handleDeleteCandidate}
                onUpdateContact={handleUpdateContact}
                onSyncToATS={handleSyncToATS}
                syncing={syncing}
                onTogglePriority={handleTogglePriority}
                isPrioritized={candidate.isPrioritized}
                hasPhone={Boolean(candidate.phone?.trim())}
              />

              {uploadNotice && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${
                  uploadNotice.type === 'success'
                    ? 'border-success/30 bg-success/10 text-success'
                    : 'border-destructive/30 bg-destructive/10 text-destructive'
                }`}>
                  {uploadNotice.message}
                </div>
              )}

              {uploadResult && !syncing && (
                <ZohoUploadResult result={uploadResult} />
              )}

              <CandidateCallsTable calls={calls} />

              <div className="flex items-center justify-between gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handleBack}
                  className="bg-white hover:bg-accent hover:text-accent-foreground"
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewProfile}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="bg-white hover:bg-accent hover:text-accent-foreground"
                >
                  {t('ai.candidate.profile')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
      {createPortal(
        <ConfirmDialog
          isOpen={showLimitDialog}
          onClose={() => setShowLimitDialog(false)}
          onConfirm={() => setShowLimitDialog(false)}
          title={t('ai.candidate.priorityLimitTitle')}
          message={t('ai.candidate.priorityLimitMessage', { max: MAX_PRIORITIZED })}
          confirmText={t('common.ok')}
          variant="default"
          hideCancel
        />,
        document.body
      )}
    </>
  );
};

export default CandidateAICallsPage;
